import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  createConversation,
  fetchAIContext,
  listContracts,
  listOutreachDrafts,
  listNotifications,
} from "../api";
import ConversationView from "../ConversationView";
import DocReviewPanel from "../components/DocReviewPanel";

// Three-column deal workspace shell.
//   Left   (180px) — compact nav (Översikt / Köpare / Dokument / Datarum / Aktivitet)
//   Center         — tab content for the active nav item
//   Right  (280px) — always-visible Sigge chat
//
// No broker terminology anywhere in the copy — the whole surface is
// framed around the seller doing their own deal with Sigge's help.
export default function DealWorkspace() {
  const { id: workspaceIdRaw } = useParams();
  const workspaceId = Number(workspaceIdRaw);
  const navigate = useNavigate();

  const [ctx, setCtx] = useState(null);
  const [tab, setTab] = useState("overview");
  const [error, setError] = useState(null);

  // Per-tab data (fetched lazily as the user clicks into each tab).
  const [drafts, setDrafts] = useState(null);
  const [contracts, setContracts] = useState(null);
  const [notifications, setNotifications] = useState(null);

  // Auto-created right-pane conversation
  const [chatConvId, setChatConvId] = useState(null);
  const chatBootRef = useRef(false);

  // Slide-in document review panel state
  const [reviewingDoc, setReviewingDoc] = useState(null); // {kind, id}

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [context, dr, ct, notif] = await Promise.all([
          fetchAIContext(),
          listOutreachDrafts({ workspaceId }),
          listContracts(workspaceId).catch(() => ({ contracts: [] })),
          listNotifications(workspaceId).catch(() => ({ notifications: [] })),
        ]);
        if (!alive) return;
        setCtx(context);
        setDrafts(dr?.drafts || []);
        setContracts(ct?.contracts || ct?.results || []);
        setNotifications(notif?.notifications || notif?.results || []);
      } catch (e) {
        if (!alive) return;
        setError(e?.body?.detail || e.message || "Kunde inte ladda affären.");
      }
    })();
    return () => {
      alive = false;
    };
  }, [workspaceId]);

  // Auto-create a workspace-scoped conversation for the right pane so
  // Sigge is one message away no matter which tab is open.
  useEffect(() => {
    if (chatBootRef.current) return;
    if (!ctx) return;
    const owned = (ctx.workspaces || []).some((w) => w.id === workspaceId);
    if (!owned) return;
    chatBootRef.current = true;

    (async () => {
      try {
        const conv = await createConversation({ workspaceId });
        setChatConvId(conv.id);
      } catch {
        /* right pane simply stays empty if conversation creation fails */
      }
    })();
  }, [ctx, workspaceId]);

  const workspace = useMemo(
    () => (ctx?.workspaces || []).find((w) => w.id === workspaceId) || null,
    [ctx, workspaceId],
  );

  const buyersByStatus = useMemo(
    () => groupBuyersByStatus(drafts || []),
    [drafts],
  );

  const pendingDocCount = useMemo(() => {
    const p1 = (drafts || []).filter((d) => d.status === "pending_review").length;
    const p2 = (contracts || []).filter((c) => c.status === "pending_review").length;
    return p1 + p2;
  }, [drafts, contracts]);

  const openDocReview = useCallback((kind, id) => {
    setReviewingDoc({ kind, id });
  }, []);

  if (error) {
    return <div className="qai-error qai-full-error">{error}</div>;
  }

  if (!ctx) {
    return <div className="qai-thread-loading">Laddar affären…</div>;
  }

  return (
    <div className="qai-dw-shell">
      {/* Left column — compact nav */}
      <aside className="qai-dw-nav">
        <div className="qai-dw-nav-brand">
          <span className="qai-brand-mark">Q</span>
          <span className="qai-dw-nav-brand-name">
            {workspace?.organisation_name || "Affären"}
          </span>
        </div>
        <NavItem
          label="Översikt"
          active={tab === "overview"}
          onClick={() => setTab("overview")}
        />
        <NavItem
          label="Köpare"
          badge={(drafts || []).length || null}
          active={tab === "buyers"}
          onClick={() => setTab("buyers")}
        />
        <NavItem
          label="Dokument"
          badge={pendingDocCount || null}
          active={tab === "docs"}
          onClick={() => setTab("docs")}
        />
        <NavItem
          label="Datarum"
          active={tab === "dataroom"}
          onClick={() => setTab("dataroom")}
        />
        <NavItem
          label="Aktivitet"
          active={tab === "activity"}
          onClick={() => setTab("activity")}
        />
        <div className="qai-dw-nav-spacer" />
        <button
          type="button"
          className="qai-dw-nav-logout"
          onClick={() => navigate("/logout")}
        >
          Logga ut
        </button>
      </aside>

      {/* Center column — tab content */}
      <main className="qai-dw-center">
        {tab === "overview" && (
          <OverviewTab workspace={workspace} drafts={drafts || []} />
        )}
        {tab === "buyers" && (
          <BuyersTab
            buyersByStatus={buyersByStatus}
            onReviewDraft={(id) => openDocReview("outreach", id)}
          />
        )}
        {tab === "docs" && (
          <DocsTab
            drafts={drafts || []}
            contracts={contracts || []}
            onReview={openDocReview}
          />
        )}
        {tab === "dataroom" && (
          <div className="qai-dw-empty">
            <h2>Datarum</h2>
            <p>Datarum — kommer snart.</p>
          </div>
        )}
        {tab === "activity" && (
          <ActivityTab notifications={notifications || []} />
        )}
      </main>

      {/* Right column — always-visible Sigge chat */}
      <aside className="qai-dw-chat">
        <div className="qai-dw-chat-head">
          <span className="qai-brand-mark">Q</span>
          <span>Fråga Sigge</span>
        </div>
        {chatConvId ? (
          <ConversationView
            key={chatConvId}
            conversationId={chatConvId}
            hideHeader
          />
        ) : (
          <div className="qai-thread-loading">Startar Sigge…</div>
        )}
      </aside>

      {reviewingDoc && (
        <DocReviewPanel
          workspaceId={workspaceId}
          docKind={reviewingDoc.kind}
          docId={reviewingDoc.id}
          onClose={() => setReviewingDoc(null)}
          onChanged={async () => {
            // Refetch the affected list so status badges update.
            if (reviewingDoc.kind === "outreach") {
              const dr = await listOutreachDrafts({ workspaceId }).catch(
                () => null,
              );
              if (dr?.drafts) setDrafts(dr.drafts);
            } else if (reviewingDoc.kind === "contract") {
              const ct = await listContracts(workspaceId).catch(() => null);
              if (ct?.contracts) setContracts(ct.contracts);
            }
          }}
        />
      )}
    </div>
  );
}

// ── Left-column nav item ─────────────────────────────────────────────

function NavItem({ label, badge, active, onClick }) {
  return (
    <button
      type="button"
      className={"qai-dw-nav-item" + (active ? " qai-dw-nav-item-active" : "")}
      onClick={onClick}
    >
      <span>{label}</span>
      {badge != null && badge !== 0 && (
        <span className="qai-dw-nav-badge">{badge}</span>
      )}
    </button>
  );
}

// ── Center-column: Overview ──────────────────────────────────────────

const STAGE_ORDER = [
  { key: "onboarding", label: "Förberedelse" },
  { key: "marketing", label: "Marknadsföring" },
  { key: "buyer_contact", label: "Intresse" },
  { key: "negotiation", label: "Förhandling" },
  { key: "closing", label: "Avslut" },
];

function OverviewTab({ workspace, drafts }) {
  const currentStage = workspace?.deal_stage || "onboarding";
  const daysActive = useMemo(() => {
    if (!workspace?.activated_at) return null;
    const diff = Date.now() - new Date(workspace.activated_at).getTime();
    return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
  }, [workspace]);

  const buyerCount = drafts.length;

  return (
    <div className="qai-dw-tab">
      <h1>Översikt</h1>

      <section className="qai-dw-card">
        <h2>Processteg</h2>
        <div className="qai-dw-timeline">
          {STAGE_ORDER.map((stage, i) => {
            const idx = STAGE_ORDER.findIndex((s) => s.key === currentStage);
            let cls = "qai-dw-timeline-step";
            if (i < idx) cls += " qai-dw-timeline-step-done";
            else if (i === idx) cls += " qai-dw-timeline-step-active";
            return (
              <div key={stage.key} className={cls}>
                <div className="qai-dw-timeline-dot" />
                <div className="qai-dw-timeline-label">{stage.label}</div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="qai-dw-card">
        <h2>Nyckeltal</h2>
        <div className="qai-dw-stats-grid">
          <Stat label="Köpare i pipeline" value={buyerCount} />
          <Stat
            label="Dagar aktiv"
            value={daysActive != null ? daysActive : "—"}
          />
          <Stat
            label="Status"
            value={workspace?.deal_status || "pending"}
          />
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="qai-dw-stat">
      <div className="qai-dw-stat-label">{label}</div>
      <div className="qai-dw-stat-value">{value}</div>
    </div>
  );
}

// ── Center-column: Buyers ────────────────────────────────────────────

const BUYER_STATUS_GROUPS = [
  { key: "draft", label: "Hittad" },
  { key: "pending_review", label: "Utkast" },
  { key: "approved", label: "Redo att skickas" },
  { key: "sent", label: "Kontaktad" },
  { key: "rejected", label: "Avböjt" },
];

function groupBuyersByStatus(drafts) {
  const grouped = new Map();
  BUYER_STATUS_GROUPS.forEach((g) => grouped.set(g.key, []));
  drafts.forEach((d) => {
    const bucket = grouped.get(d.status) || grouped.get("draft");
    bucket.push(d);
  });
  return grouped;
}

function BuyersTab({ buyersByStatus, onReviewDraft }) {
  const hasAny = Array.from(buyersByStatus.values()).some((list) => list.length > 0);
  return (
    <div className="qai-dw-tab">
      <h1>Köpare</h1>
      {!hasAny && (
        <div className="qai-dw-empty">
          <p>
            Inga köpare än. Be Sigge (till höger) att söka lämpliga köpare
            baserat på din affär.
          </p>
        </div>
      )}
      {BUYER_STATUS_GROUPS.map((g) => {
        const items = buyersByStatus.get(g.key) || [];
        if (items.length === 0) return null;
        return (
          <section key={g.key} className="qai-dw-card">
            <h2>
              {g.label}{" "}
              <span className="qai-dw-count">{items.length}</span>
            </h2>
            <div className="qai-dw-buyer-list">
              {items.map((d) => (
                <BuyerRow key={d.id} draft={d} onReview={onReviewDraft} />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function BuyerRow({ draft, onReview }) {
  const score = draft.match_score || 0;
  let badgeCls = "qai-dw-score qai-dw-score-low";
  if (score >= 0.7) badgeCls = "qai-dw-score qai-dw-score-high";
  else if (score >= 0.5) badgeCls = "qai-dw-score qai-dw-score-mid";
  const name = draft.acquirer?.name || "Okänd köpare";
  return (
    <div className="qai-dw-buyer">
      <div className="qai-dw-buyer-main">
        <div className="qai-dw-buyer-name">
          {name}
          <span className={badgeCls}>{(score * 100).toFixed(0)}</span>
        </div>
        <div className="qai-dw-buyer-sub">
          {draft.subject || "Utkast till outreach"}
        </div>
      </div>
      <button
        type="button"
        className="qai-onboard-primary qai-dw-buyer-action"
        onClick={() => onReview(draft.id)}
      >
        Granska
      </button>
    </div>
  );
}

// ── Center-column: Docs ──────────────────────────────────────────────

function DocsTab({ drafts, contracts, onReview }) {
  const rows = useMemo(() => {
    const out = [];
    drafts.forEach((d) =>
      out.push({
        key: `d-${d.id}`,
        kind: "outreach",
        id: d.id,
        title: d.subject || d.acquirer?.name || "Outreach-utkast",
        status: d.status,
        updated_at: d.updated_at || d.created_at,
      }),
    );
    contracts.forEach((c) =>
      out.push({
        key: `c-${c.id}`,
        kind: "contract",
        id: c.id,
        title: c.title || `${c.contract_type || "Avtal"} – ${c.id}`,
        status: c.status,
        updated_at: c.updated_at || c.created_at,
      }),
    );
    out.sort((a, b) => String(b.updated_at).localeCompare(String(a.updated_at)));
    return out;
  }, [drafts, contracts]);

  if (rows.length === 0) {
    return (
      <div className="qai-dw-tab">
        <h1>Dokument</h1>
        <div className="qai-dw-empty">
          <p>Inga dokument än. Be Sigge att skapa ett utkast.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="qai-dw-tab">
      <h1>Dokument</h1>
      <div className="qai-dw-doc-list">
        {rows.map((r) => (
          <div key={r.key} className="qai-dw-doc">
            <div className="qai-dw-doc-main">
              <div className="qai-dw-doc-title">{r.title}</div>
              <div className="qai-dw-doc-sub">
                <span className="qai-dw-doc-kind">
                  {r.kind === "outreach" ? "Outreach" : "Kontrakt"}
                </span>
                <span
                  className="qai-outreach-status-chip"
                  data-status={r.status}
                >
                  {r.status}
                </span>
              </div>
            </div>
            <button
              type="button"
              className="qai-onboard-primary qai-dw-doc-action"
              onClick={() => onReview(r.kind, r.id)}
            >
              Granska
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Center-column: Activity ──────────────────────────────────────────

function ActivityTab({ notifications }) {
  if (notifications.length === 0) {
    return (
      <div className="qai-dw-tab">
        <h1>Aktivitet</h1>
        <div className="qai-dw-empty">
          <p>Inga aktiviteter loggade än.</p>
        </div>
      </div>
    );
  }
  return (
    <div className="qai-dw-tab">
      <h1>Aktivitet</h1>
      <div className="qai-dw-activity">
        {notifications.map((n) => (
          <div key={n.id} className="qai-dw-activity-row">
            <div className="qai-dw-activity-time">
              {n.created_at ? new Date(n.created_at).toLocaleString("sv-SE") : ""}
            </div>
            <div className="qai-dw-activity-title">{n.title || n.kind}</div>
            {n.body && (
              <div className="qai-dw-activity-body">{n.body}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
